import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { ApprovalFlowDto, ApprovalState, EquipmentTabDto, FindingDescriptionTabDto, GeneralTabDto, IResolutionAndMitigationPlanDto, ReportType, RiptTabDto } from '../../api/api-clientInformation';
import { ReportFormPage } from './report-form-control.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { RiskEvaluationFormData } from '../components/report-form/tabs/risk-evaluation/models';

const DEFAULT_RISK_SCORE = 10;


export enum StageField {
  Draft = 0,
  AreaLeadApproval = 1,
  SupervisorApproval = 2
}
export interface ReportFormState {
  ript: RiptTabDto | null,
  risk: RiskEvaluationFormData | null,
  general: GeneralTabDto | null;
  equipment: EquipmentTabDto | null;
  findingDescription: FindingDescriptionTabDto | null;
  mitigationResolution: IResolutionAndMitigationPlanDto | null;
  approvalFlow: ApprovalFlowDto | null;
  saving: boolean;
  page: ReportFormPage | null,
  isStepValid: boolean,
  stepValidity: Record<string, boolean>,
  closeReport: boolean,
  status: string | null;
  step: string | null;
  stageField: StageField | null;
}

@Injectable({ providedIn: 'root' })
export class ReportFormStore extends ComponentStore<ReportFormState> {

  readonly saving$: Observable<boolean> = this.select(state => state.saving);
  
  readonly page$: Observable<ReportFormPage | null> = this.select(state => state.page);

  page = toSignal(this.page$); 

  readonly isStepValid$: Observable<boolean> = this.select(state => state.page ? !!state.stepValidity[state.page] : false);
  readonly closeReport$: Observable<boolean> = this.select(state => state.closeReport);


  readonly stageField$: Observable<StageField | null> = this.select(state => state.stageField);

  readonly stageField = toSignal(this.stageField$);
  /**
   * General Tab selector
   */
  readonly general$: Observable<GeneralTabDto | null> = this.select(state => state.general);
  
  readonly approvalState$ = this.select(this.general$, general => general?.currentState);

  /**
   * General Tab: Report Type selector
   */
  readonly reportType$: Observable<ReportType | undefined> = this.select(state => state.general?.reportType);

  /**
   * Get ReportType as Signal
   */
  readonly reportType = toSignal(this.reportType$);

  /**
   * General Tab: GeneralLTabId
   */
  readonly generalTabId$: Observable<string | undefined> = this.select(state => state.general?.id);

  /**
   * Risk Evaluation Tab
   */
  readonly risk$: Observable<RiskEvaluationFormData | null> = this.select(state => state.risk);

  /**
   * Risk Evaluation Tab: High Score
   */
  readonly riskHighScore$: Observable<number | null> = this.select(this.risk$, risk => risk?.riskEvaluationDtos.reduce((min, item) => {
    const consequence = item.consequence ?? 0;
    const likelihood = item.likelihood ?? 0;
    const total = consequence + likelihood;
    const score = total > DEFAULT_RISK_SCORE ? DEFAULT_RISK_SCORE : total - 1;

    if (score > 0 && min > score) {
      return score;
    }

    return min;
  }, DEFAULT_RISK_SCORE) ?? null);

  /**
   * Equipment Tab selector
   */
  readonly equipment$: Observable<EquipmentTabDto | null> = this.select(state => state.equipment);
  readonly equipment = toSignal(this.equipment$);
  readonly ript$: Observable<RiptTabDto | null> = this.select(state => state.ript);
  readonly findingDescription$: Observable<FindingDescriptionTabDto | null> = this.select(state => state.findingDescription);
  readonly mitigationResolution$: Observable<IResolutionAndMitigationPlanDto | null> = this.select(state => state.mitigationResolution);

  /**
   * Approval Flow Tab selector
   */
  readonly approvalFlow$: Observable<ApprovalFlowDto | null> = this.select(state => state.approvalFlow);

  readonly criticality$: Observable<string | null> = this.select(this.equipment$, (equipment) => equipment?.consequenceRanking ?? null);
  readonly criticality = toSignal(this.criticality$);
  // readonly consequenceRanking$: Observable<string | null> = this.select(state => state.consequenceRanking);
  readonly assignedManagementAuthorizationLevel$: Observable<string | null> = this.select(this.equipment$, (equipment) => equipment?.assignedManagementAuthorizationLevel ?? null);

  readonly assignedManagementAuthorizationLevel = toSignal(this.assignedManagementAuthorizationLevel$);
  readonly abcPrioritization$: Observable<string | undefined> = this.select(state => state.ript?.abcPrioritization);

  readonly abcPrioritization = toSignal(this.abcPrioritization$);

  /**
   * Set store's initial value
   */
  constructor() {
    super({
      ript: null,
      risk: null,
      general: null,
      equipment: null,
      findingDescription: null,
      mitigationResolution: null,
      approvalFlow: null,
      saving: false,
      page: null,
      isStepValid: false,
      stepValidity: {},
      closeReport: false,
      status: null,
      step: null,
      stageField: null
    });
  }

  readonly restore = this.updater(() => ({
    ript: null,
    risk: null,
    general: null,
    equipment: null,
    findingDescription: null,
    mitigationResolution: null,
    approvalFlow: null,
    saving: false,
    page: null,
    isStepValid: false,
    stepValidity: {},
    closeReport: false,
    status: null,
    step: null,
    stageField: null
  }));

  readonly setStageField = this.updater((state, stageField: StageField | null) => {
    if (stageField !== null && !Object.values(StageField).includes(stageField)) {
      return state; // Return unchanged state for invalid values
    }

    return { ...state, stageField };
  });

  /**
   * Store General Tab data
   */
  readonly setGeneral = this.updater((state, general: GeneralTabDto) => ({ ...state, general }));

  /**
   * Patch only currentState on the stored GeneralTabDto without full reload.
   */
  readonly patchCurrentState = this.updater((state, currentState: ApprovalState) => ({
    ...state,
    general: state.general ? GeneralTabDto.fromJS({ ...state.general, currentState }) : state.general
  }));

  /**
   * Store General Tab data
   */
  readonly setReportType = this.updater((state, reportType: ReportType | null) => ({
    ...state,
    general: GeneralTabDto.fromJS({ ...state.general, reportType })
  }));

  /**
   * Store General Tab data
   */
  readonly setResolutionTargetDate = this.updater((state, resolutionTargetDate: Date | string) => ({
    ...state,
    general: GeneralTabDto.fromJS({ ...state.general, resolutionTargetDate })
  }));

  /**
   * Store Equipment Tab data
   */
  readonly setEquipment = this.updater((state, equipment: EquipmentTabDto) => ({ ...state, equipment }));
  readonly setRipt = this.updater((state, ript: RiptTabDto) => ({ ...state, ript }));
  
  /**
   * Store Finding Description Tab data
   */
  readonly setFindingDescription = this.updater((state, findingDescription: FindingDescriptionTabDto) => ({ ...state, findingDescription }));
  
  readonly setMitigationResolution = this.updater((state, mitigationResolution: any) => ({ ...state, mitigationResolution }));

  readonly setConsequenceRanking = this.updater((state, consequenceRanking: string | null) => ({
    ...state,
    equipment: EquipmentTabDto.fromJS({ ...state.equipment, consequenceRanking })
  }));
  readonly setAssignedManagementAuthorizationLevel = this.updater((state, assignedManagementAuthorizationLevel: string | null) => ({
    ...state,
    equipment: EquipmentTabDto.fromJS({ ...state.equipment, assignedManagementAuthorizationLevel })
  }));

  readonly setAbcPrioritization = this.updater((state, abcPrioritization: string | undefined) => ({
    ...state,
    ript: state.ript ? RiptTabDto.fromJS({ ...state.ript, abcPrioritization }) : RiptTabDto.fromJS({ abcPrioritization })
  }));

  /**
   * Trigger Save event
   */
  readonly save = this.updater((state, payload: { page: ReportFormPage, closeReport?: boolean }) => ({
    ...state,
    saving: true,
    page: payload.page,
    closeReport: payload.closeReport ?? false,
  }));

  readonly closeReport = this.updater((state, closeReport: boolean ) => ({ ...state, closeReport }));

  /**
   * Change page
   */
  readonly changePage = this.updater((state, page: ReportFormPage) => ({
    ...state,
    page,
    isStepValid: !!state.stepValidity[page]
  }));

  /**
   * Reset save flag
   */
  readonly reset = this.updater((state) => ({ ...state, saving: false }));

  /**
   * Store page validity status
   */
  readonly setPageValidity = this.updater((state, payload: { page: string, isStepValid: boolean }) => {
    const stepValidity = { ...state.stepValidity, [payload.page]: payload.isStepValid };
    const currentPageValidity = state.page ? !!stepValidity[state.page] : false;

    return {
      ...state,
      stepValidity,
      isStepValid: currentPageValidity
    };
  });

  /**
   * Store Risk Evaluation Tab data
   */
  readonly setRisk = this.updater((state, risk: RiskEvaluationFormData) => ({ ...state, risk }));

  /**
   * Store Approval Flow Tab data
   */
  readonly setApprovalFlow = this.updater((state, approvalFlow: ApprovalFlowDto) => ({ ...state, approvalFlow }));
}
